import React, { useContext } from "react";
import styled from "styled-components";
import moment from "moment";
import iconCheck from "../../../../../../images/iconCheck.png";
import { ThemeContext } from "styled-components";

const SurveyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 20px 0px;
  min-width: 500px;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 auto 10px auto;
`;

const Description = styled.p`
  font-size: 1rem;
  width: 100%;
  text-align: center;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 20px 0px;
  width: 100%;
`;

const QuestionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
`;

const QuestionDescription = styled.p`
  color: #555;
  font-size: 0.9rem !important;
`;

const Checkmark = styled.img`
  width: 26px;
  height: 26px;
  margin: 0 !important;
`;

const Circle = styled.div`
  border: 1px solid #555;
  width: 25px;
  height: 25px;
  border-radius: 50%;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  gap: 10px;
  margin-bottom: 3px;
  p {
    margin: 0 !important;
  }
`;

const ScaleResponseContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  max-width: 100%;
  overflow-x: auto;
  padding: 2px 0;
`;

const ScaleLabel = styled.p`
  flex-shrink: 0;
  margin: 0 !important;
`;

const ScaleOptions = styled.div`
  border: 1px solid #ccc;
  display: flex;
  flex-shrink: 0;
`;

const ScaleOption = styled.div`
  align-items: center;
  background: ${(props) => props.$selected ? props.theme.color.primary : "white"};
  border-right: 1px solid #ccc;
  color: ${(props) => props.$selected ? "white" : "inherit"};
  display: flex;
  font-size: 1.125rem;
  height: 48px;
  justify-content: center;
  width: 48px;

  &:last-child {
    border-right: none;
  }
`;

const CheckInContainer = styled.div`
  background-color: #f1f3f5;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 10px;
  width: 100%;
`;

const CheckInSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CheckInTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const CheckInDetail = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DetailLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
`;

const DetailValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #333;
`;

const PhotosContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 8px;
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PhotoLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: capitalize;
`;

const PhotoImage = styled.img`
  width: 160px;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const NoPermissionText = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
  text-align: center;
`;

const kgToLbs = (kg) => {
  return (kg * 2.204622).toFixed(1);
};

const calculateLeanBodyMass = (weight, bodyFat) => {
  const leanMass = weight * (1 - bodyFat / 100);
  return leanMass.toFixed(1);
};

const formatDate = (date) => {
  return moment.utc(date).format("MMM D, YYYY");
};

const CheckInDetailItem = ({ label, value }) => (
  <CheckInDetail>
    <DetailLabel>{label}:</DetailLabel>
    <DetailValue>{value}</DetailValue>
  </CheckInDetail>
);

const LinearScaleResponse = ({ question, response }) => {
  const min = Number.isFinite(Number(question.min)) ? Number(question.min) : 1;
  const max = Number.isFinite(Number(question.max)) ? Number(question.max) : 5;
  const selectedValue = String(response.response);
  const options = [];

  for (let value = min; value <= max; value += 1) {
    options.push(
      <ScaleOption key={value} $selected={String(value) === selectedValue}>
        {value}
      </ScaleOption>
    );
  }

  return (
    <ScaleResponseContainer aria-label={`Selected scale response: ${selectedValue}`}>
      {question.minLabel && <ScaleLabel>{question.minLabel}</ScaleLabel>}
      <ScaleOptions>{options}</ScaleOptions>
      {question.maxLabel && <ScaleLabel>{question.maxLabel}</ScaleLabel>}
    </ScaleResponseContainer>
  );
};

export const SurveyResponse = ({ data = {}, checkInData = {} }) => {
  const theme = useContext(ThemeContext);
  const { survey_data, response_data } = data;

  const renderResponse = (question, response) => {
    switch (question.responseType) {
      case "text":
        return <p>{response.response}</p>;
      case "multiple_choice":
      case "checkbox":
        const options = question?.options?.map((option) => {
          const isChecked = response?.response.includes(option.id);
          return (
            <Option key={option.id}>
              {isChecked ? (
                <Checkmark src={iconCheck} alt="check" />
              ) : (
                <Circle />
              )}
              <p>{option.value}</p>
            </Option>
          );
        });
        return options;
      case "linear_scale":
        return <LinearScaleResponse question={question} response={response} />;
      case "select_check_in":
        const checkIn = checkInData[question.id];
        if (checkIn) {
          return (
            <CheckInContainer>
              {checkIn.url_list && checkIn.url_list.length > 0 && (
                <CheckInSection>
                  <PhotosContainer>
                    {checkIn.url_list.map((urls, index) => {
                      const photoLabel = checkIn.photo_list[index]
                        ? checkIn.photo_list[index][0]
                        : `Photo ${index + 1}`;
                      const thumbnailUrl = urls[1];
                      const fullUrl = urls[0];

                      return (
                        <PhotoItem key={index}>
                          <PhotoImage
                            src={thumbnailUrl}
                            alt={`${photoLabel} progress photo`}
                            onClick={() => window.open(fullUrl, "_blank")}
                          />
                          <PhotoLabel>{photoLabel}</PhotoLabel>
                        </PhotoItem>
                      );
                    })}
                  </PhotosContainer>
                </CheckInSection>
              )}
              {checkIn?.trainer_permission_photos === 0 && (
                <NoPermissionText>
                  This client has not allowed you to view their check-in photos.
                </NoPermissionText>
              )}
              <CheckInSection>
                <CheckInTitle>Check-in Details</CheckInTitle>
                <CheckInDetailItem
                  label="Report Date"
                  value={formatDate(checkIn.report_date)}
                />
                <CheckInDetailItem
                  label="Weight"
                  value={`${kgToLbs(checkIn.weight)} lbs`}
                />
                <CheckInDetailItem
                  label="Body Fat"
                  value={`${checkIn.body_fat}%`}
                />
                <CheckInDetailItem
                  label="Lean Body Mass"
                  value={`${kgToLbs(
                    calculateLeanBodyMass(checkIn.weight, checkIn.body_fat)
                  )} lbs`}
                />
                <CheckInDetailItem
                  label="Goal"
                  value={theme.goal[checkIn.goal]}
                />
                <CheckInDetailItem
                  label="Protein"
                  value={`${checkIn.protein}g`}
                />
                <CheckInDetailItem label="Carbs" value={`${checkIn.carbs}g`} />
                <CheckInDetailItem label="Fat" value={`${checkIn.fat}g`} />
                <CheckInDetailItem
                  label="Calories"
                  value={`${
                    checkIn.protein * 4 + checkIn.carbs * 4 + checkIn.fat * 9
                  } Cal`}
                />
              </CheckInSection>
            </CheckInContainer>
          );
        } else {
          return <p>Loading check-in data...</p>;
        }
      default:
        return null;
    }
  };

  return (
    <SurveyContainer>
      <Title>{survey_data?.title}</Title>
      <Description>{survey_data?.description}</Description>
      {survey_data?.questions?.map((question) => {
        const response = response_data.find(
          (r) => r.question_id === question.id
        );
        return (
          <QuestionContainer key={question.id}>
            <QuestionTitle>{question.title}</QuestionTitle>
            <QuestionDescription>{question.description}</QuestionDescription>
            {response ? (
              renderResponse(question, response)
            ) : (
              <p style={{ fontStyle: "italic" }}>No response</p>
            )}
          </QuestionContainer>
        );
      })}
    </SurveyContainer>
  );
};
